import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { t } from '@fetmode/i18n';
import { useAuth } from './AuthProvider.js';
import { AuthModal } from './AuthModal.js';
import { useVisitor } from '../visitor/index.js';
const AuthModalContext = createContext(null);
const INTENT_MESSAGE_KEYS = {
    tip: 'common.auth.intentTip',
    join: 'common.auth.intentJoin',
    community: 'common.auth.intentCommunity',
    private: 'common.auth.intentPrivate',
    privateChat: 'common.auth.intentPrivateChat',
    wallet: 'common.auth.intentWallet',
    fanClub: 'common.auth.intentFanClub',
    purchase: 'common.auth.intentPurchase',
    follow: 'common.auth.intentFollow',
    game: 'common.auth.intentGame',
    toys: 'common.auth.intentToys',
    jukebox: 'common.auth.intentJukebox',
    generic: 'common.auth.intentGeneric',
};
export function authIntentMessage(intent) {
    if (!intent) {
        return null;
    }
    return t(INTENT_MESSAGE_KEYS[intent]);
}
export function AuthModalProvider({ children }) {
    const { user } = useAuth();
    const { isReturningVisitor } = useVisitor();
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState('register');
    const [intent, setIntent] = useState(null);
    const [onSuccess, setOnSuccess] = useState(null);
    const closeAuthModal = useCallback(() => {
        setIsOpen(false);
        setIntent(null);
        setOnSuccess(null);
    }, []);
    const openAuthModal = useCallback((options) => {
        const defaultMode = isReturningVisitor ? 'login' : 'register';
        setMode(options?.mode ?? defaultMode);
        setIntent(options?.intent ?? null);
        const successCallback = options?.onSuccess ?? null;
        setOnSuccess(() => successCallback);
        setIsOpen(true);
    }, [isReturningVisitor]);
    const requireAuth = useCallback((successCallback, nextIntent = 'generic') => {
        if (user) {
            successCallback?.();
            return true;
        }
        openAuthModal({ intent: nextIntent, onSuccess: successCallback });
        return false;
    }, [openAuthModal, user]);
    const value = useMemo(() => ({
        openAuthModal,
        closeAuthModal,
        requireAuth,
    }), [closeAuthModal, openAuthModal, requireAuth]);
    return (_jsxs(AuthModalContext.Provider, { value: value, children: [children, _jsx(AuthModal, { isOpen: isOpen, initialMode: mode, intent: intent, intentMessage: authIntentMessage(intent), isReturningVisitor: isReturningVisitor, onClose: closeAuthModal, onSuccess: () => {
                    onSuccess?.();
                } })] }));
}
export function useAuthModal() {
    const context = useContext(AuthModalContext);
    if (!context) {
        throw new Error('useAuthModal must be used within AuthModalProvider');
    }
    return context;
}
//# sourceMappingURL=AuthModalProvider.js.map